import { Component, DestroyRef, inject, Injector, input, OnInit, output } from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { LaserService } from './laser-service';
import { LaserTreatmentControl } from './laser-treatment-control';
import { RxIconModule } from '@nrwl/rx-icon';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { catchError, filter, forkJoin, map, merge, Observable, of, take } from 'rxjs';
import { LaserWidgetMetadata } from './laser-widget-metadata';
import { LaserTreatmentItem } from './laser-treatment-item';
import { LaserEquipment } from './laser-equipment';
import { LaserOption } from './laser-option';
import { LaserProcedureType } from './laser-procedure-type';
import { LaserTreatment } from './laser-treatment';
import { LaserTreatmentControlValue } from './laser-treatment-control-value';
import { Company } from './company';
import { Treatment } from './treatment';
import { ExpansionPanel } from './expansion-panel';
import { ExpansionPanelHeader } from './expansion-panel-header';
import { ExpansionPanelContent } from './expansion-panel-content';
import { DatePipe } from '@angular/common';
import { findBestLaserComparableOption } from '../../../helpers/laser-comparable';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';
import { copyScribeSuggestionMarker } from '../../scribe-suggestion-marker.util';

interface LaserSuggestionPatch {
  date?: unknown;
  equipment?: unknown;
  procedureType?: unknown;
  serviceTime?: unknown;
  smartCool?: unknown;
  joules?: unknown;
  pulse?: unknown;
  repetitionRate?: unknown;
  firstLaserOption?: unknown;
  secondLaserOption?: unknown;
  note?: unknown;
}

@Component({
  selector: 'fb-laser-widget-edit',
  imports: [
    ReactiveFormsModule,
    RxIconModule,
    LaserTreatmentControl,
    ExpansionPanel,
    ExpansionPanelHeader,
    ExpansionPanelContent,
    DatePipe,
  ],
  templateUrl: './laser-widget-edit.html',
  styleUrl: './laser-widget-edit.scss',
})
export class LaserWidgetEdit implements OnInit {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly laserService = inject(LaserService);

  /**
   * Existing laser data.
   */
  public readonly data = input<readonly LaserTreatmentItem[]>([]);
  public readonly markerTargets = input<readonly (ScribeSuggestionMarkerTarget | null)[]>([]);

  /**
   * ID of the appointment.
   */
  public readonly appointmentId = input<string | undefined>();

  /**
   * ID of the treatment.
   */
  public readonly treatmentId = input<string | undefined>();

  /**
   * Patient remote ID.
   */
  public readonly patientRemoteId = input<string | undefined>();

  /**
   * Emits whether the payload should be updated.
   */
  public readonly update = output<readonly LaserTreatmentItem[]>();

  /**
   * Form instance.
   */
  protected readonly form = this.fb.group({
    treatments: this.fb.array<FormControl<LaserTreatmentControlValue>>([]),
  });

  /**
   * Options for the laser widget.
   */
  protected readonly options = rxResource({
    request: () => {
      const patientRemoteId = this.patientRemoteId();
      const appointmentId = this.appointmentId();
      const treatmentId = this.treatmentId();
      const data = this.data();

      return {
        data,
        patientRemoteId,
        treatmentId,
        appointmentId,
      };
    },
    loader: ({ request }) => {
      if (!request.patientRemoteId || !request.appointmentId) {
        return of(this.buildEmptyOptionsState());
      }

      return forkJoin([
        this.getTreatmentObservable(request.treatmentId, request.appointmentId),
        this.getLaserTreatmentsObservable(request.data, request.patientRemoteId),
        this.laserService.getCompany().pipe(catchError(() => of(this.buildEmptyCompany()))),
        this.laserService
          .getLaserEquipment()
          .pipe(catchError(() => of<readonly LaserEquipment[]>([]))),
        this.laserService
          .getLaserProcedureTypes()
          .pipe(catchError(() => of<readonly LaserProcedureType[]>([]))),
        this.laserService.getLaserOptions().pipe(catchError(() => of<readonly LaserOption[]>([]))),
      ]).pipe(
        map(([treatment, treatments, company, equipment, procedureType, laser]) => ({
          treatment,
          treatments,
          company,
          equipment,
          procedureType,
          laser,
        })),
      );
    },
  });

  private expandedSuggestionIndexes = new Set<number>();

  public ngOnInit(): void {
    this.observeChanges();
    this.observeOptions();
  }

  private buildEmptyOptionsState() {
    return {
      treatment: null,
      treatments: [] as readonly LaserTreatment[],
      company: this.buildEmptyCompany(),
      equipment: [] as readonly LaserEquipment[],
      procedureType: [] as readonly LaserProcedureType[],
      laser: [] as readonly LaserOption[],
    };
  }

  private buildEmptyCompany(): Company {
    return {
      smartCoolText: null,
      joulesText: null,
      pulseText: null,
      repetitionRateText: null,
      firstLaserOptionText: null,
      secondLaserOptionText: null,
    };
  }

  /**
   * Returns the metadata for the laser widget.
   */
  public getMetadata(): LaserWidgetMetadata | null {
    const value = this.options.value();

    if (!value) {
      return null;
    }

    return {
      treatmentId: value.treatment?.id ?? null,
      records: this.form.controls.treatments.value.map((record) => {
        const result: LaserTreatmentItem = {
          id: record.id,
          treatmentId: record.treatmentId,
          date: record.date,
          equipmentId: record.equipment ? record.equipment.id : null,
          equipmentName: record.equipment ? record.equipment.name : null,
          procedureTypeId: record.procedureType ? record.procedureType.id : null,
          procedureTypeName: record.procedureType ? record.procedureType.name : null,
          smartCool: record.smartCool,
          joules: record.joules,
          repetitionRate: record.repetitionRate,
          serviceTime: record.serviceTime,
          pulse: record.pulse,
          note: record.note,
          firstLaserOptionId: record.firstLaserOption ? record.firstLaserOption.id : null,
          firstLaserOptionName: record.firstLaserOption ? record.firstLaserOption.text : null,
          secondLaserOptionId: record.secondLaserOption ? record.secondLaserOption.id : null,
          secondLaserOptionName: record.secondLaserOption ? record.secondLaserOption.text : null,
          labels: {
            smartCoolText: value.company.smartCoolText,
            joulesText: value.company.joulesText,
            pulseText: value.company.pulseText,
            repetitionRateText: value.company.repetitionRateText,
            firstLaserOptionText: value.company.firstLaserOptionText,
            secondLaserOptionText: value.company.secondLaserOptionText,
          },
        };
        copyScribeSuggestionMarker(
          record as unknown as ScribeSuggestionMarkerTarget,
          result as unknown as ScribeSuggestionMarkerTarget,
        );
        return result;
      }),
    };
  }

  public applySuggestionSet(patch: LaserSuggestionPatch): void {
    const options = this.options.value();
    if (!options) {
      return;
    }

    const targetIndex = this.ensureSuggestionTargetIndex();
    const control = this.form.controls.treatments.at(targetIndex);
    if (!control) {
      return;
    }

    const nextValue = this.buildPatchedTreatmentValue(control.value, patch, options);
    control.setValue(nextValue);
    control.markAsDirty();
    control.markAsTouched();
    this.expandedSuggestionIndexes.add(targetIndex);
  }

  public areScribeOptionsLoaded(): boolean {
    return !this.options.isLoading() && this.options.hasValue();
  }

  public getLaserEquipmentOptionsSnapshot(): Array<{ id: string; name: string }> {
    const options = this.options.value();
    return Array.isArray(options?.equipment)
      ? options.equipment
          .map((item) => ({
            id: `${item?.id ?? ''}`.trim(),
            name: `${item?.name ?? ''}`.trim(),
          }))
          .filter((item) => !!item.id && !!item.name)
      : [];
  }

  public getLaserProcedureTypeOptionsSnapshot(): Array<{ id: string; name: string }> {
    const options = this.options.value();
    return Array.isArray(options?.procedureType)
      ? options.procedureType
          .map((item) => ({
            id: `${item?.id ?? ''}`.trim(),
            name: `${item?.name ?? ''}`.trim(),
          }))
          .filter((item) => !!item.id && !!item.name)
      : [];
  }

  public getLaserOptionsByTypeSnapshot(): {
    first: Array<{ id: string; text: string }>;
    second: Array<{ id: string; text: string }>;
  } {
    const laser = this.options.value()?.laser ?? [];
    const first = laser
      .filter((item) => item?.type === 1)
      .map((item) => ({
        id: `${item?.id ?? ''}`.trim(),
        text: `${item?.text ?? ''}`.trim(),
      }))
      .filter((item) => !!item.id && !!item.text);
    const second = laser
      .filter((item) => item?.type === 2)
      .map((item) => ({
        id: `${item?.id ?? ''}`.trim(),
        text: `${item?.text ?? ''}`.trim(),
      }))
      .filter((item) => !!item.id && !!item.text);

    return { first, second };
  }

  public isValid(): boolean {
    return this.form ? this.form.valid : true;
  }

  /**
   * Adds a new treatment to the form.
   */
  protected addLaserTreatment(): void {
    const control = this.fb.control<LaserTreatmentControlValue>(this.getEmptyLaserTreatmentValue());

    this.form.controls.treatments.push(control);
  }

  /**
   * Whether the control should be expanded.
   */
  protected shouldBeExpanded(
    index: number,
    value: LaserTreatmentControlValue,
    treatment: Treatment | null,
  ): boolean {
    if (this.expandedSuggestionIndexes.has(index)) {
      return true;
    }

    if (treatment === null) {
      return false;
    }

    return value.treatmentId === treatment.id;
  }

  /**
   * Adds existing records to the form.
   */
  private syncFormControls(
    equipment: readonly LaserEquipment[],
    laser: readonly LaserOption[],
    procedureType: readonly LaserProcedureType[],
    treatments: readonly LaserTreatment[],
  ): void {
    const records = [...this.data(), ...treatments];

    if (records.length === 0) {
      const control = this.fb.control<LaserTreatmentControlValue>(
        this.getEmptyLaserTreatmentValue(),
      );

      this.form.controls.treatments.push(control);

      return;
    }

    records.forEach((record) => {
      const value: LaserTreatmentControlValue = {
        id: record.id,
        treatmentId: record.treatmentId,
        date: record.date,
        equipment: equipment.find((option) => option.id === record.equipmentId) || null,
        procedureType: procedureType.find((option) => option.id === record.procedureTypeId) || null,
        smartCool: record.smartCool,
        joules: record.joules,
        pulse: record.pulse,
        repetitionRate: record.repetitionRate,
        serviceTime: record.serviceTime,
        firstLaserOption:
          laser.find((option) => option.type === 1 && option.id === record.firstLaserOptionId) ||
          null,
        secondLaserOption:
          laser.find((option) => option.type === 2 && option.id === record.secondLaserOptionId) ||
          null,
        note: record.note,
      };
      copyScribeSuggestionMarker(
        record as unknown as ScribeSuggestionMarkerTarget,
        value as unknown as ScribeSuggestionMarkerTarget,
      );
      const control = this.fb.control<LaserTreatmentControlValue>(value);

      this.form.controls.treatments.push(control);
    });
  }

  /**
   * Returns an empty laser treatment value.
   */
  private getEmptyLaserTreatmentValue(): LaserTreatmentControlValue {
    const getTreatmentId = () => {
      if (this.options.hasValue() && this.options.value().treatment) {
        return this.options.value().treatment.id;
      }

      return null;
    };

    return {
      id: null,
      treatmentId: getTreatmentId(),
      date: new Date(),
      equipment: null,
      procedureType: null,
      smartCool: null,
      joules: null,
      pulse: null,
      repetitionRate: null,
      serviceTime: null,
      firstLaserOption: null,
      secondLaserOption: null,
      note: '',
    };
  }

  /**
   * Observes options and updates form controls.
   */
  private observeOptions(): void {
    toObservable(this.options.value, { injector: this.injector })
      .pipe(filter(Boolean), take(1))
      .subscribe(({ equipment, laser, procedureType, treatments }) =>
        this.syncFormControls(equipment, laser, procedureType, treatments),
      );
  }

  /**
   * Observes changes and updates payload.
   */
  private observeChanges(): void {
    merge(this.form.valueChanges, this.form.statusChanges)
      .pipe(
        map(() => this.getMetadata()),
        filter(Boolean),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ records }) => this.update.emit(records));
  }

  private ensureSuggestionTargetIndex(): number {
    const controls = this.form.controls.treatments.controls;
    const reusableIndex = controls.findIndex(
      (control) => !this.isTreatmentMeaningful(control.value),
    );

    if (reusableIndex >= 0) {
      return reusableIndex;
    }

    const control = this.fb.control<LaserTreatmentControlValue>(this.getEmptyLaserTreatmentValue());
    this.form.controls.treatments.push(control);
    return this.form.controls.treatments.length - 1;
  }

  private isTreatmentMeaningful(value: LaserTreatmentControlValue): boolean {
    return !!(
      value.equipment ||
      value.procedureType ||
      value.serviceTime !== null ||
      value.smartCool !== null ||
      value.joules !== null ||
      value.pulse !== null ||
      value.repetitionRate !== null ||
      value.firstLaserOption ||
      value.secondLaserOption ||
      value.note.trim()
    );
  }

  private buildPatchedTreatmentValue(
    current: LaserTreatmentControlValue,
    patch: LaserSuggestionPatch,
    options: {
      equipment: readonly LaserEquipment[];
      procedureType: readonly LaserProcedureType[];
      laser: readonly LaserOption[];
    },
  ): LaserTreatmentControlValue {
    return {
      ...current,
      ...(patch.date !== undefined
        ? { date: this.resolveSuggestedDateValue(patch.date) ?? current.date }
        : {}),
      ...(patch.equipment !== undefined
        ? {
            equipment: this.resolveLaserSelection(
              options.equipment,
              patch.equipment,
              (option) => option.name,
            ),
          }
        : {}),
      ...(patch.procedureType !== undefined
        ? {
            procedureType: this.resolveLaserSelection(
              options.procedureType,
              patch.procedureType,
              (option) => option.name,
            ),
          }
        : {}),
      ...(patch.serviceTime !== undefined
        ? { serviceTime: this.resolveSuggestedNumberValue(patch.serviceTime) }
        : {}),
      ...(patch.smartCool !== undefined
        ? { smartCool: this.resolveSuggestedNumberValue(patch.smartCool) }
        : {}),
      ...(patch.joules !== undefined
        ? { joules: this.resolveSuggestedNumberValue(patch.joules) }
        : {}),
      ...(patch.pulse !== undefined
        ? { pulse: this.resolveSuggestedNumberValue(patch.pulse) }
        : {}),
      ...(patch.repetitionRate !== undefined
        ? { repetitionRate: this.resolveSuggestedNumberValue(patch.repetitionRate) }
        : {}),
      ...(patch.firstLaserOption !== undefined
        ? {
            firstLaserOption: this.resolveLaserSelection(
              options.laser.filter((option) => option.type === 1),
              patch.firstLaserOption,
              (option) => option.text,
            ),
          }
        : {}),
      ...(patch.secondLaserOption !== undefined
        ? {
            secondLaserOption: this.resolveLaserSelection(
              options.laser.filter((option) => option.type === 2),
              patch.secondLaserOption,
              (option) => option.text,
            ),
          }
        : {}),
      ...(patch.note !== undefined ? { note: `${patch.note ?? ''}`.trim() } : {}),
    };
  }

  private resolveSuggestedNumberValue(value: unknown): number | null {
    if (value === null || value === undefined || `${value}`.trim() === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private resolveSuggestedDateValue(value: unknown): Date | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    if (typeof value !== 'string' || !value.trim()) {
      return null;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private resolveLaserSelection<T extends { id: string }>(
    options: readonly T[],
    value: unknown,
    getLabel: (option: T) => string,
  ): T | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (`${value}`.trim() === '') {
      return null;
    }

    return findBestLaserComparableOption(
      options,
      value,
      (option) => getLabel(option),
      (option) => option.id,
    );
  }

  /**
   * Returns an observable for the treatment based on data availability.
   */
  private getTreatmentObservable(
    treatmentId: string | undefined,
    appointmentId: string | undefined,
  ): Observable<Treatment | null> {
    if (this.isDefined(treatmentId)) {
      return of({ id: treatmentId });
    }

    if (this.isDefined(appointmentId)) {
      return this.laserService
        .getTreatment({
          appointmentId: appointmentId,
        })
        .pipe(catchError(() => of(null)));
    }

    return of(null);
  }

  /**
   * Returns an observable for the laser treatments based on data availability.
   */
  private getLaserTreatmentsObservable(
    data: readonly LaserTreatmentItem[],
    patientRemoteId: string | undefined,
  ): Observable<readonly LaserTreatment[]> {
    if (data.length > 0) {
      return of<readonly LaserTreatment[]>([]);
    }

    return this.laserService
      .getLaserTreatments({
        customerId: patientRemoteId,
      })
      .pipe(catchError(() => of<readonly LaserTreatment[]>([])));
  }

  /**
   * Checkes whether the value is defined.
   */
  private isDefined(value: string | undefined | null): value is string {
    return value !== undefined && value !== null && value !== '';
  }
}
